import { selectCurrentHotelId } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoBar from 'components/ReusableComponents/InfoBar'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import NewBadge from 'components/ReusableComponents/NewBadge'
import NewFeatureBox from 'components/ReusableComponents/NewFeatureBox'
import RestrictedDateWarningCampaigns from 'components/ReusableComponents/RestrictedDateWarning'
import Tab from 'components/ReusableComponents/Tabs'
import Toggle from 'components/ReusableComponents/Toggle'
import AssistDripProposalDialog from 'features/CustomCampaigns/AssistDripProposalDialog'
import { customCampaignStatusTypes } from 'features/CustomCampaigns/helpers'
import AudienceCard from 'features/CustomCampaigns/List/AudienceCard'
import CampaignCard from 'features/CustomCampaigns/List/CampaignCard'
import NewAudience from 'features/CustomCampaigns/NewAudience'
import NewCampaign from 'features/CustomCampaigns/NewCampaign'
import DripCountIndicator from 'features/ManageCampaigns/DripCountIndicator'
import useCampaignCredits from 'hooks/useCampaignCredits'
import usePlausible from 'hooks/usePlausible'
import { useInternalAdmin } from 'hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import audienceApi from 'services/api/audience'
import campaignApi from 'services/api/campaign'
import llmPlatformApi from 'services/api/llmPlatform'
import {
  clearAssistDripProposalDismiss,
  dismissAssistDripProposal,
  isAssistDripProposalDismissed
} from 'features/CustomCampaigns/assistDripProposalDismiss'
import { resolveDefaultCampaignAudienceId } from 'features/CustomCampaigns/campaignAudienceDefaults'

import styles from './CustomCampaignsList.module.scss'

const CustomCampaignsList = () => {
  const subsidiaryId = useSelector(selectCurrentHotelId)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)
  const isInternalAdmin = useInternalAdmin()

  const [audiences, setAudiences] = useState([])
  const [audiencesError, setAudiencesError] = useState()
  const [isAudiencesLoading, setIsAudiencesLoading] = useState(true)
  const [isSelectAudienceDialogVisible, setIsSelectAudienceDialogVisible] =
    useState(false)
  const [isCreateNewCampaignDialogVisible, setIsCreateNewCampaignDialogVisible] =
    useState(false)

  const [newAudienceName, setNewAudienceName] = useState('')
  const { credits: dripCount, refetch: refetchCredits } = useCampaignCredits()
  function handleCloseNewAudienceDialog() {
    setIsCreateNewCampaignDialogVisible(false)
    setNewAudienceName('')
  }

  const [showDeleted, setShowDeleted] = useState(false)
  const [hasDeletedCampaigns, setHasDeletedCampaigns] = useState(false)

  const history = useHistory()

  const { addToast } = useToasts()

  const { newParam } = useParams()

  const [restrictedTextingDates, setRestrictedTextingDates] = useState(null)

  const { postEvent, plausibleEvents } = usePlausible()

  useEffect(() => {
    audienceApi
      .getList()
      .then(res => {
        setAudiences(res)
        setAudiencesError(null)
      })
      .catch(err => {
        setAudiencesError(err)
      })
      .finally(() => {
        setIsAudiencesLoading(false)
      })
  }, [])

  function createCampaign({ audienceId, deliveryName }) {
    postEvent(plausibleEvents.CreateNewTextBlast, {
      deliveryName
    })

    const payload = { deliveryName }
    if (audienceId) {
      payload.audienceId = audienceId
    }

    campaignApi
      .addCustomCampaign(payload)
      .then(res => {
        history.push(`/custom-campaigns/${res.item.contentId}`)
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error'
        })
      })
  }

  const [campaigns, setCampaigns] = useState([])
  const [campaignsError, setCampaignsError] = useState()
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true)

  const [refreshKey, setRefreshKey] = useState(0)

  const [dripProposal, setDripProposal] = useState(null)
  const [dripProposalReasoning, setDripProposalReasoning] = useState('')
  const [isDripProposalLoading, setIsDripProposalLoading] = useState(false)
  const [showDripProposalDialog, setShowDripProposalDialog] = useState(false)
  const dripProposalFetchedRef = useRef(false)

  const loadDripProposal = useCallback(
    ({ force = false } = {}) => {
      if (!subsidiaryId || audiences.length === 0) {
        return Promise.resolve(null)
      }

      if (!force && isAssistDripProposalDismissed(subsidiaryId)) {
        return Promise.resolve(null)
      }

      setIsDripProposalLoading(true)

      const audiencePayload = audiences
        .filter(a => !a.isDeleted)
        .map(a => ({
          id: a.id,
          name: a.name,
          size: a.size || 0,
          isDeleted: !!a.isDeleted
        }))

      return llmPlatformApi
        .getAssistDripProposal(subsidiaryId, audiencePayload, dripCount || 0)
        .then(res => {
          if (res?.proposal) {
            setDripProposal(res.proposal)
            setDripProposalReasoning(res.reasoning || '')
            return res.proposal
          }
          return null
        })
        .catch(() => {
          // Proposal is optional — do not block the page
          return null
        })
        .finally(() => {
          setIsDripProposalLoading(false)
        })
    },
    [subsidiaryId, audiences, dripCount]
  )

  useEffect(() => {
    campaignApi
      .getCustomCampaignList()
      .then(res => {
        setCampaigns(res.items)
        setRestrictedTextingDates(res.restrictedTextingDates)
        setHasDeletedCampaigns(
          res.items.find(
            c => c.customDeliveryStatus === customCampaignStatusTypes.Deleted
          )
            ? true
            : false
        )
      })
      .catch(err => {
        setCampaignsError(err)
      })
      .finally(() => {
        setIsCampaignsLoading(false)
      })
  }, [refreshKey])

  useEffect(() => {
    if (
      isAudiencesLoading ||
      isCampaignsLoading ||
      !subsidiaryId ||
      audiences.length === 0 ||
      dripProposalFetchedRef.current
    ) {
      return
    }

    if (isAssistDripProposalDismissed(subsidiaryId)) {
      return
    }

    dripProposalFetchedRef.current = true

    loadDripProposal().then(proposal => {
      if (proposal) {
        setShowDripProposalDialog(true)
      }
    })
  }, [
    isAudiencesLoading,
    isCampaignsLoading,
    subsidiaryId,
    audiences,
    dripCount,
    loadDripProposal
  ])

  useEffect(() => {
    function handlePageShow(e) {
      if (e.persisted) {
        setRefreshKey(k => k + 1)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  if (isCampaignsLoading || isAudiencesLoading) {
    return <LoadingIndicator />
  }

  const visibleCampaigns = campaigns
    ?.sort((a, b) => b.contentId - a.contentId)
    ?.filter(c =>
      showDeleted ? true : c.customDeliveryStatus !== customCampaignStatusTypes.Deleted
    )

  function handleClickNewCampaign() {
    if (audiences.length > 0) {
      setIsCreateNewCampaignDialogVisible(true)
    } else {
      setIsSelectAudienceDialogVisible(true)
    }
  }

  const handleDismissDripProposal = () => {
    dismissAssistDripProposal(subsidiaryId)
    setShowDripProposalDialog(false)
  }

  const handleDripProposalSuccess = contentId => {
    setShowDripProposalDialog(false)
    setRefreshKey(k => k + 1)
    history.push(`/custom-campaigns/${contentId}`)
  }

  const handleShowDripProposal = () => {
    if (dripProposal) {
      setShowDripProposalDialog(true)
      return
    }

    clearAssistDripProposalDismiss(subsidiaryId)
    loadDripProposal({ force: true }).then(proposal => {
      if (proposal) {
        setShowDripProposalDialog(true)
      }
    })
  }

  return (
    <div className={styles.wrapper}>
      {showDripProposalDialog && dripProposal && (
        <AssistDripProposalDialog
          proposal={dripProposal}
          reasoning={dripProposalReasoning}
          onClose={handleDismissDripProposal}
          onSuccess={handleDripProposalSuccess}
          isHotelSetupCompleted={isHotelSetupCompleted}
        />
      )}
      {isDripProposalLoading && !showDripProposalDialog && (
        <div className={styles.proposalLoading}>
          <LoadingIndicator />
          <p>Preparing a drip campaign suggestion...</p>
        </div>
      )}
      {newParam === 'new-campaign' && <NewCampaign audiences={audiences} />}
      {newParam === 'new-audience' && <NewAudience />}
      {isCreateNewCampaignDialogVisible && (
        <ConfirmationDialog
          title="Create new campaign"
          hasConfirmButton={true}
          hasCloseButton={true}
          positiveMessage="Continue"
          onPressEnter={() =>
            newAudienceName.length > 0
              ? createCampaign({
                  audienceId: resolveDefaultCampaignAudienceId(audiences),
                  deliveryName: newAudienceName
                })
              : false
          }
          negativeMessage="Cancel"
          theme="blacklist"
          isButtonDisabled={newAudienceName.length === 0}
          onReject={() => handleCloseNewAudienceDialog()}
          onClose={() => handleCloseNewAudienceDialog()}
          onConfirm={() =>
            createCampaign({
              audienceId: resolveDefaultCampaignAudienceId(audiences),
              deliveryName: newAudienceName
            })
          }
        >
          <TextInput
            label="Drip campaign name"
            className={styles.textBlasNameInput}
            autoFocus={true}
            value={newAudienceName}
            onChange={e => setNewAudienceName(e.target.value)}
          />
        </ConfirmationDialog>
      )}

      {isSelectAudienceDialogVisible && (
        <ConfirmationDialog
          hasCloseButton={true}
          hasConfirmButton={false}
          title="Select an audience for your new campaign"
          theme="blacklist"
          onClose={() => setIsSelectAudienceDialogVisible(false)}
        >
          <div className={styles.audiencesButtonsHolder}>
            {audiences
              .filter(a => !a.isDeleted)
              .map(a => (
                <button
                  className={styles.audienceButton}
                  onClick={() => createCampaign({ audienceId: a.id })}
                >
                  <p className={styles.audienceButtonName}>{a.name}</p>
                  <p className={styles.audienceButtonSize}>{a.size} guests</p>
                </button>
              ))}
            <button className={styles.audienceButton}>
              <Link
                to="/custom-campaigns/new-audience"
                className={styles.audienceButtonName}
              >
                Create a new audience
              </Link>
              <p></p>
            </button>
          </div>
        </ConfirmationDialog>
      )}
      <CustomHeader title="Automated messages" width={1000} isFixed={true} />
      <Tab.Parent>
        <Tab to={`manageCampaigns/sales`}>Invite messages </Tab>
        <Tab to={`manageCampaigns/service`}>In-property messages</Tab>
        <Tab to="/custom-campaigns">
          Drip campaigns
          <NewBadge className={styles.newBadge} />
        </Tab>
      </Tab.Parent>
      {/* Always show balance strip so zero credits still surfaces the indicator and staff can add credits. */}
      <DripCountIndicator
        dripCount={dripCount}
        setIsCreateNewCampaignDialogVisible={setIsCreateNewCampaignDialogVisible}
        onCreditsUpdated={refetchCredits}
      />
      <InfoBar text="Effective Nov 1, 2025, Drip Campaign message pricing is $0.06 each" />
      {isInternalAdmin && (
        <div className={styles.assistProposalAction}>
          <button
            type="button"
            className={styles.assistProposalButton}
            onClick={handleShowDripProposal}
            disabled={isDripProposalLoading}
          >
            View Assist suggestion
          </button>
        </div>
      )}
      <DataIsReprocessingBar />
      {restrictedTextingDates?.length > 0 && (
        <RestrictedDateWarningCampaigns restrictedTextingDates={restrictedTextingDates} />
      )}
      <div className={styles.content}>
        <div className={styles.audiences}>
          <hgroup>
            <h2 className={styles.title}>Audiences</h2>
            <Link to="/custom-campaigns/new-audience">
              <button className={styles.createButton}>New audience</button>
            </Link>
          </hgroup>
          {audiencesError && (
            <p className={styles.errorText}>{audiencesError.toString()}</p>
          )}
          {audiences?.length === 0 && (
            <p className={styles.errorText}>No audiences defined</p>
          )}

          {audiences?.length > 0 && (
            <div className={styles.audienceCards}>
              {audiences
                .filter(a => !a.isDeleted)
                .map(a => (
                  <AudienceCard key={a.id} name={a.name} size={a.size} id={a.id} />
                ))}
            </div>
          )}
        </div>
        <div className={styles.campaigns}>
          <hgroup>
            <h2 className={styles.title}>Campaigns</h2>
            {visibleCampaigns?.length > 0 && (
              <>
                <button className={styles.createButton} onClick={handleClickNewCampaign}>
                  New campaign
                </button>
              </>
            )}
            {hasDeletedCampaigns && (
              <Toggle
                className={styles.toggle}
                mini
                label="Show deleted"
                checked={showDeleted}
                handleChange={e => setShowDeleted(e.target.checked)}
              />
            )}
          </hgroup>
          <div className={styles.campaignCardsHolder}>
            {campaignsError && (
              <p style={{ marginLeft: 8 }} className={styles.errorText}>
                {campaignsError.toString()}
              </p>
            )}
            {visibleCampaigns?.length === 0 && (
              <div style={{ paddingLeft: 10, paddingTop: 10 }}>
                <NewFeatureBox
                  title="Create your first Drip campaign"
                  onClickAction={handleClickNewCampaign}
                  desc={
                    <>
                      <p className={styles.getStartedP}>
                        Create a campaign to send messages directly. If you have an event,
                        want to reach locals or just want to invite more people.
                      </p>
                      <p className={styles.getStartedP}>
                        Let's start with creating your first drip campaign.
                      </p>
                    </>
                  }
                />
              </div>
            )}
            {visibleCampaigns.map(c => (
              <CampaignCard
                key={c.contentId}
                name={c.deliveryName}
                remaining={c.deliveryRemaining}
                size={c.deliverySize}
                startDate={c.deliveryStartDate}
                finishDate={c.deliveryFinishDate}
                id={c.contentId}
                messageTemplate={c.messageTemplate}
                minNights={c.minNightsToStay}
                maxDiscount={c.maxDiscountPercentage}
                promotionCode={c.promotionCode}
                customDeliveryStatus={c.customDeliveryStatus}
                refreshResults={() => setRefreshKey(k => k + 1)}
                audienceId={c.audienceId}
                messageMediaUrls={c.messageMediaUrls}
                minNightsToStay={c.minNightsToStay}
                maxDiscountPercentage={c.maxDiscountPercentage}
                promotionTagline={c.promotionTagline}
                addedOn={c.addedOn}
                addedBy={c.addedBy}
                updatedOn={c.updatedOn}
                updatedBy={c.updatedBy}
                medium={c.medium}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomCampaignsList
